import React, { useContext, useState, FormEvent, useEffect } from "react";
import { motion } from "motion/react";

import { personalInfo, socialNetworkData } from "../constants";
import { ThemeContext } from "../themeProvider";
import { getEmailProvider, getProviderInfo } from "../services/email";
import type { EmailData } from "../services/email";
import { useRecaptcha } from "../hooks/useRecaptcha";

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

const Contact = () => {
  const theme = useContext(ThemeContext);
  const darkMode = theme.state.darkMode;
  
  // reCAPTCHA es OPCIONAL - solo se usa si está configurado
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaEnabled = !!recaptchaSiteKey; // true si hay site key configurado
  const { isReady: recaptchaReady, error: recaptchaError, executeRecaptcha } = useRecaptcha(recaptchaSiteKey);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  
  const [status, setStatus] = useState<FormStatus>({
    type: 'idle',
    message: ''
  });

  useEffect(() => {
    // Mostrar qué proveedor está activo (solo en desarrollo)
    const info = getProviderInfo();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📧 Email Provider Active:', info.name);
      if (recaptchaEnabled) {
        console.log('🔒 reCAPTCHA Status:', recaptchaReady ? 'Ready' : 'Loading...');
        if (recaptchaError) {
          console.error('⚠️ reCAPTCHA Error:', recaptchaError);
        }
      } else {
        console.log('⚠️ reCAPTCHA: Disabled (no REACT_APP_RECAPTCHA_SITE_KEY configured)');
      }
    }

    // Inicializar el proveedor si tiene método initialize
    const provider = getEmailProvider();
    if (provider.initialize) {
      provider.initialize().catch(err => {
        console.error('Error initializing email provider:', err);
      });
    }
  }, [recaptchaEnabled, recaptchaReady, recaptchaError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      // reCAPTCHA es OPCIONAL - solo se ejecuta si está configurado
      let recaptchaToken: string | null = null;
      
      if (recaptchaEnabled) {
        setStatus({ type: 'loading', message: 'Validando seguridad...' });
        
        if (recaptchaReady) {
          recaptchaToken = await executeRecaptcha('contact_form');
          if (!recaptchaToken) {
            console.warn('⚠️ reCAPTCHA token no generado, continuando sin validación...');
          }
        } else {
          console.warn('⚠️ reCAPTCHA no está listo, continuando sin validación...');
        }
      }

      setStatus({ type: 'loading', message: 'Enviando mensaje...' });

      const provider = getEmailProvider();
      const emailData: EmailData = {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        recaptchaToken: recaptchaToken || undefined,
      };

      const result = await provider.sendEmail(emailData);

      if (result.success) {
        setStatus({ 
          type: 'success', 
          message: '¡Mensaje enviado! Te responderé pronto.' 
        });
        
        // Limpiar formulario
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
      
      // Resetear estado después de 5 segundos
      setTimeout(() => {
        setStatus({ type: 'idle', message: '' });
      }, 5000);

    } catch (error: any) {
      setStatus({ 
        type: 'error', 
        message: error.message || 'Error al enviar el mensaje. Por favor intenta de nuevo.' 
      });
      
      setTimeout(() => {
        setStatus({ type: 'idle', message: '' });
      }, 5000);
    }
  };

  return (
    <div
      id="contact"
      className={`
        pt-20 pb-0
        ${darkMode
          ? "bg-gray-900"
          : "bg-gray-50"
        }`}
    >
      <div className="max-w-7xl mx-auto x-4 sm:px-6 lg:px-8 px-4 ">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
            darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
          }`}>
            Get In Touch
          </span>
          <h2 className={`text-5xl md:text-6xl font-bold mb-6 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Let's Work Together
          </h2>
          <p className={`text-xl max-w-2xl mx-auto ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Have a project in mind or just want to say hi? I'd love to hear from you.
            Drop me a message and I'll get back to you as soon as possible.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className={`p-8 rounded-2xl shadow-xl ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <h3 className={`text-2xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>Send me a message</h3>
            
            {status.type !== 'idle' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-xl ${
                  status.type === 'success' 
                    ? 'bg-green-500/20 border border-green-500 text-green-600' 
                    : status.type === 'error'
                    ? 'bg-red-500/20 border border-red-500 text-red-600'
                    : 'bg-blue-500/20 border border-blue-500 text-blue-600'
                }`}
              >
                <p className="font-medium">{status.message}</p>
              </motion.div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className={`block mb-2 text-sm font-semibold ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={status.type === 'loading'}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                    darkMode 
                      ? 'bg-gray-900 border-gray-700 text-white focus:border-blue-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className={`block mb-2 text-sm font-semibold ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={status.type === 'loading'}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                    darkMode 
                      ? 'bg-gray-900 border-gray-700 text-white focus:border-blue-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className={`block mb-2 text-sm font-semibold ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={status.type === 'loading'}
                  rows={5}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 resize-none ${
                    darkMode 
                      ? 'bg-gray-900 border-gray-700 text-white focus:border-blue-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                  placeholder="Tell me about your project..."
                  required
                />
              </div>
              <button 
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
              <p className={`text-center text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Or{' '}
                <a 
                  href={`mailto:${personalInfo.email}`}
                  className="text-blue-500 hover:text-blue-600 font-semibold underline"
                >
                  email me directly
                </a>
              </p>
              <p className={`text-xs text-center ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
              </p>
            </form>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className={`p-8 rounded-2xl shadow-xl ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">📱</span>
                  </div>
                  <div className="ml-4">
                    <h4 className={`text-sm font-semibold uppercase tracking-wide mb-1 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Phone</h4>
                    <a 
                      href={`tel:${personalInfo.phone}`}
                      className="text-lg font-semibold text-blue-500 hover:text-blue-600"
                    >
                      {personalInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">✉️</span>
                  </div>
                  <div className="ml-4">
                    <h4 className={`text-sm font-semibold uppercase tracking-wide mb-1 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Email</h4>
                    <a 
                      href={`mailto:${personalInfo.email}`}
                      className="text-lg font-semibold text-blue-500 hover:text-blue-600 break-all"
                    >
                      {personalInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">📍</span>
                  </div>
                  <div className="ml-4">
                    <h4 className={`text-sm font-semibold uppercase tracking-wide mb-1 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>Location</h4>
                    <p className={`text-lg font-semibold ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {personalInfo.address.length > 0 && `${personalInfo.address}, `}
                      {personalInfo.city}, {personalInfo.country}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`p-8 rounded-2xl shadow-xl ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>Follow Me</h3>
              <div className="flex gap-4">
                {socialNetworkData.map((el) => (
                  <motion.a 
                    key={el.name} 
                    href={el.url} 
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className={`p-4 rounded-xl transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gray-900 hover:bg-gray-700' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    } shadow-md hover:shadow-xl`}
                  >
                    <img alt={el.name} src={el.image} className="w-8 h-8" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className={`
          w-full text-center py-8 mt-20 border-t
          ${darkMode
            ? " bg-gray-800 text-gray-400 border-gray-700"
            : " bg-white text-gray-600 border-gray-200"
          }`}
      >
        <p className="text-sm">
          Designed & Built by{' '}
          <span className={`font-semibold ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>{personalInfo.name}</span>
        </p>
        <p className="text-xs mt-2">
          © {new Date().getFullYear()} All rights reserved.
        </p>
      </motion.div>
    </div>
  );
};

export default Contact;
